import { Button } from "@/components/design-system/button";
import { getAssignmentByCourseService } from "@/service/assignment.service";
import { updateSubmissionStatusService } from "@/service/submission.service";
import { selectUser, selectUserRole } from "@/store/slices/authSlice";
import {
  AssignmentQuery,
  CourseAssignmentDetail,
} from "@/types/assignment.model";
import { hasRolePermissions, ROLE_PERMISSIONS, ROLES } from "@/utils/constants";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import { AssignmentCard } from "./AssingmentCard";
import FeedbackForm from "./FeedbackForm";
import SubmissionForm from "./SubmissionForm";
import { markNotificationAsReadAction } from "@/store/slices/courseSlice";

interface AssignmentsProps {
  selectedAssignmentType: string;
  selectedStudentId: string;
  selectedLessonId: string;
  selectedUnitId: string;
  recordType?: "course" | "class";
}

const Assignments = ({
  selectedAssignmentType,
  selectedStudentId,
  selectedLessonId,
  selectedUnitId,
  recordType = "course",
}: AssignmentsProps) => {
  const { id } = useParams<{ id: string }>();
  const user = useSelector(selectUser);
  const userRole = useSelector(selectUserRole);
  const location = useLocation();
  const processedCardRef = useRef<string | null>(null);

  const dispatch = useDispatch();

  const [assignments, setAssignments] = useState<CourseAssignmentDetail[]>([]);
  const [expandedAssignments, setExpandedAssignments] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const toggleAssignment = async (assignmentId: string) => {
    if (expandedAssignments.includes(assignmentId)) {
      setExpandedAssignments(
        expandedAssignments.filter(
          (assignmentId) => assignmentId !== assignmentId
        )
      );
    } else {
      setExpandedAssignments([...expandedAssignments, assignmentId]);

      try {
        await updateSubmissionStatusService({ assignmentId: assignmentId });
        dispatch(
          // @ts-ignore
          markNotificationAsReadAction({
            assignment_id: assignmentId,
            student_id: selectedStudentId || user?.student_user_id,
            gradeId: id,
            viewType: recordType,
          })
        );
      } catch (error) {
        console.error("Failed to update submission status:", error);
      }
    }
  };

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const params: AssignmentQuery = {
        page: 1,
        pageSize: 50,
        studentId: selectedStudentId || user?.student_user_id,
        schoolId: user?.school_id,
        teacherId: user?.teacher_id,
        type: selectedAssignmentType as "general" | "unit",
        recordType,
        ...(recordType === "class"
          ? { classId: id as string }
          : { gradeId: id as string }),
      };

      if (selectedAssignmentType && selectedAssignmentType === "unit") {
        params.lessonId = selectedLessonId || "";
        params.unitId = selectedUnitId || "";
      }

      const {
        data: { data, pageInfo },
      } = await getAssignmentByCourseService(params);

      setAssignments(data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setAssignments([]);
      }

      console.error("Error fetching assignments:", error);
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch course details when component mounts or id changes
  useEffect(() => {
    if (
      id &&
      (selectedStudentId || user?.student_user_id) &&
      (selectedAssignmentType === "general" ||
        (selectedAssignmentType === "unit" &&
          selectedLessonId &&
          selectedUnitId))
    ) {
      fetchAssignments();
    }

    // Cleanup function to reset current course when component unmounts
  }, [
    id,
    recordType,
    selectedAssignmentType,
    selectedLessonId,
    selectedUnitId,
    selectedStudentId,
    user,
  ]);

  useEffect(() => {
    // Only process if we have a selectedCardId and it's different from what we've already processed
    if (
      location.state?.selectedTab === "assignment" &&
      location.state?.selectedCardId &&
      processedCardRef.current !== location.state.selectedCardId
    ) {
      processedCardRef.current = location.state.selectedCardId;
      toggleAssignment(location.state.selectedCardId);
    }
  }, [location.state?.selectedCardId, location.state?.selectedTab]);

  return (
    <div className="w-full bg-white py-[12px] rounded-xl overflow-hidden">
      <div className="flex items-center px-[24px] justify-between p-2 border-b border-[#EEEEEE] flex-wrap sm:flex-nowrap">
        <h2 className="text-xl font-semibold text-[#1F1F1F]">
          List of Assignment
        </h2>
      </div>
      {/* [calc(100vh-370px)] overflow-y-auto */}
      <div className="px-[24px] py-[25px] lg:h-[55rem] h-[45rem] overflow-y-auto">
        {!loading ? (
          <>
            {assignments.length > 0 ? (
              <>
                {assignments.map((assignment) => (
                  <div key={assignment.id}>
                    <div className="mb-[6px]">
                      <div
                        className={`flex h-[50px] justify-between items-center px-[12px] sm:px-[24px] py-[13px] rounded-xl cursor-pointer w-full ${
                          expandedAssignments.includes(assignment.id)
                            ? "bg-[#DEEAF7]"
                            : "bg-white border border-[#EEEEEE]"
                        }`}
                        onClick={() => toggleAssignment(assignment.id)}
                      >
                        <h3 className="text-xl font-semibold text-[#1F1F1F]">
                          {assignment.name}
                        </h3>
                        <div className="flex items-center gap-2 sm:gap-3.5 flex-shrink-0">
                          {assignment.status === "NEW" && (
                            <button className="w-[98px] h-[32px] bg-white text-[#5C88C5] border border-[#5C88C5] rounded-full text-[16px] font-medium">
                              New
                            </button>
                          )}
                          {expandedAssignments.includes(assignment.id) ? (
                            <ChevronUp className="w-6 h-6 text-[#1C1B1F]" />
                          ) : (
                            <ChevronDown className="w-6 h-6 text-[#1C1B1F]" />
                          )}
                        </div>
                      </div>

                      {expandedAssignments.includes(assignment.id) && (
                        <>
                          <div className="p-3 sm:p-6 border-x border-b rounded-xl border-[#EEEEEE] w-full overflow-x-auto">
                            <AssignmentCard assignment={assignment} />
                          </div>

                          {!assignment.is_closed &&
                            userRole?.code === ROLES.TEACHER && (
                              <FeedbackForm
                                assignment={assignment}
                                onRefresh={fetchAssignments}
                              />
                            )}

                          {!assignment.is_closed &&
                            userRole?.code === ROLES.STUDENT && (
                              <SubmissionForm
                                assignment={assignment}
                                onRefresh={fetchAssignments}
                              />
                            )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div
                className={`flex flex-col mb-2 justify-center items-center h-1/2`}
              >
                {userRole?.code === ROLES.SUPER_ADMIN && (
                  <>
                    <p className="text-[#1F1F1F] text-[18px] font-bold mb-[9px]">
                      No Assignments assigned yet.
                    </p>
                  </>
                )}

                {userRole?.code === ROLES.STUDENT && (
                  <>
                    <p className="text-[#1F1F1F] text-[18px] font-bold mb-[9px]">
                      No Assignments assigned yet.
                    </p>

                    <div>
                      <p className="text-[#1F1F1F] text-[18px] font-medium mb-[28px]">
                        Your teacher-assigned tasks will appear here.
                      </p>
                    </div>
                  </>
                )}

                {userRole?.code === ROLES.TEACHER && (
                  <>
                    <p className="text-[#1F1F1F] text-[18px] font-bold mb-[9px]">
                      No Assignments yet.
                    </p>

                    {hasRolePermissions(userRole?.permissions as string[], [
                      ROLE_PERMISSIONS.BUTTON_ADD_ASSIGNMENT,
                    ]) && (
                      <>
                        <p className="text-[#1F1F1F] text-[18px] font-medium mb-[28px]">
                          Start by creating assignments and assigning them to
                          students.
                        </p>
                        <Button
                          className="w-[195px] h-[48px] bg-[#5C88C5] text-white"
                          onClick={() =>
                            navigate("/assignments/add-assignment")
                          }
                        >
                          Add Assignment
                        </Button>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        ) : (
          <div
            className={`flex flex-col mb-2 justify-center items-center h-1/2`}
          >
            <p className="mt-4 text-gray-600">Loading Assignments...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Assignments;
